import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import { AppContext } from "../utilities/context";
import { FormattedMessage, useIntl } from "react-intl";
import axios from "axios";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import Container from "@mui/material/Container";
import Layout from "./Layout";
import Grid from "@mui/material/Grid";
import { Button, Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";

const Categories = () => {
  const intl = useIntl();
  const context = useContext(AppContext);
  const [categories, setCategories] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [totalRows, setTotalRows] = useState(0);
  const [sortModel, setSortModel] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "" });

  const getCategories = () => {
    const { page, pageSize } = paginationModel;
    let sortParams = "";
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      sortParams = `&sortField=${field}&sortOrder=${sort}`;
    }
    axios.get(`/api/categories?page=${page}&pageSize=${pageSize}${sortParams}`)
      .then(res => {
        setCategories(res.data.categories);
        setTotalRows(res.data.total);
      })
      .catch(err => console.error(err));
  };

  useEffect(() => {
    getCategories();
    if (context) {
      context.updateBreadcrumbsState([
        { label: "Home", url: "/" },
        { label: "Categorías" },
      ]);
    }
  }, [paginationModel, sortModel]);

  const handleOpenCreate = () => {
    setEditingCategory(null);
    setFormData({ name: "", description: "" });
    setModalOpen(true);
  };

  const handleOpenEdit = (id) => {
    const cat = categories.find(c => c.id === id);
    if (cat) {
      setEditingCategory(cat);
      setFormData({ name: cat.name, description: cat.description || "" });
      setModalOpen(true);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("¿Eliminar esta categoría?")) {
      try {
        await axios.delete(`/api/categories/${id}`);
        getCategories();
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleSave = async () => {
    try {
      if (editingCategory) {
        await axios.put(`/api/categories/${editingCategory.id}`, formData);
      } else {
        await axios.post("/api/categories", formData);
      }
      setModalOpen(false);
      getCategories();
    } catch (err) {
      console.error(err);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "name", headerName: "Nombre", flex: 1 },
    { field: "description", headerName: "Descripción", flex: 1 },
    { field: "slug", headerName: "Slug", width: 150 },
    { field: "level", headerName: "Nivel", width: 80, type: "number" },
    { field: "isActive", headerName: "Activo", width: 80, type: "boolean" },
    {
      field: "actions",
      type: "actions",
      headerName: "Acciones",
      width: 100,
      getActions: ({ id }) => [
        <GridActionsCellItem icon={<EditIcon />} label="Edit" onClick={() => handleOpenEdit(id)} color="inherit" />,
        <GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={() => handleDelete(id)} color="inherit" />,
      ],
    },
  ];

  return (
    <Layout>
      <Container maxWidth={false} disableGutters>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={1}></Grid>
          <Grid item xs={12} sm={10} sx={{ textAlign: "right" }}>
            <Button variant="contained" size="small" onClick={handleOpenCreate} sx={{ mr: 1 }}>
              Crear
            </Button>
          </Grid>
          <Grid item xs={12} sm={1}></Grid>
          <Grid item xs={12} sm={1}></Grid>
          <Grid item xs={12} sm={10} sx={{ textAlign: "center" }}>
            <Box sx={{ height: 400, width: "100%" }}>
              <DataGrid
                rows={categories}
                columns={columns}
                pagination
                paginationMode="server"
                sortingMode="server"
                rowCount={+totalRows}
                pageSizeOptions={[5, 10, 25, 50]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                sortModel={sortModel}
                onSortModelChange={(model) => setSortModel(model)}
                disableRowSelectionOnClick
                getRowId={(row) => row.id}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={1}></Grid>
        </Grid>
      </Container>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCategory ? "Editar Categoría" : "Nueva Categoría"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Nombre"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Descripción"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Categories;
